import React from 'react';
import { useTranslations } from '@unisporkal/localization';
import { useExperience } from '@unisporkal/experiences';
import {
  Button,
  ButtonSizeEnum,
  ButtonVariantEnum,
  CheckRoundedIcon,
  List,
  ListItem,
  Typography,
  TypographyComponentEnum,
  TypographyVariant,
} from '@unisporkal/alliance-ui';
import { ExpandableBuyCard } from '../../../molecules/ExpandableBuyCard';
import { ButtonGroupSelector } from '../../../molecules/ButtonGroupSelector';
import { CaretText } from '../../../molecules/CaretText';
import useAcquisitionModal from '../../../../hooks/useAcquisitionModal';
import {
  trackClickViewLicenseDetailsCreativeUp,
  trackClickViewLicenseDetailsEditorialUp,
} from '../../../../support/ctaTracking';
import {
  SINGLE_IMAGE_CREATIVE_LICENSE_MODAL_ID,
  SINGLE_IMAGE_EDITORIAL_LICENSE_MODAL_ID,
  ULTRAPACK_LICENSE_DETAILS_MODAL_ID,
} from '../../../../constants/modalConstants';
import { type AcquisitionModalState } from '../../../../types/acquisitionModalState';
import { type UltraPackBuyCardOption } from '../../../../../types/isomorphic';
import styles from './UltrapackBuyCard.module.scss';

export type UltrapackBuyCardProps = {
  packOptions: UltraPackBuyCardOption[];
  selectedPackSize: number | null;
  onSelectPack: (packSize: number) => void;
  isEditorial?: boolean;
  showSavingsBadge?: boolean;
  isExpanded: boolean;
  isSelected: boolean;
  onExpand: () => void;
  onSelect: () => void;
};

const BENEFIT_TRANSLATION_KEYS = [
  'millions_creative_images_illustrations_videos_photos',
  'no_auto-renew_annual_commitments',
  'includes_all_sizes_formats',
];

const VIEW_LICENSE_DETAILS_TRANSLATION_KEY = 'view_license_details';

const UltrapackBuyCard = ({
  packOptions,
  selectedPackSize,
  onSelectPack,
  isEditorial,
  showSavingsBadge = false,
  isExpanded,
  isSelected,
  onExpand,
  onSelect,
}: UltrapackBuyCardProps) => {
  const t = useTranslations();
  const { setModal } = useAcquisitionModal() as AcquisitionModalState;
  const subsCopyUpdatesActive = useExperience(
    'GettySubsCopyUpdates'
  ).isActive();

  const selectedPack =
    packOptions.find((option) => option.packSize === selectedPackSize) ??
    packOptions[0];

  const handleLicenseDetailsClick = () => {
    const trackViewLicenseDetails = isEditorial
      ? trackClickViewLicenseDetailsEditorialUp
      : trackClickViewLicenseDetailsCreativeUp;
    trackViewLicenseDetails(VIEW_LICENSE_DETAILS_TRANSLATION_KEY);
    if (subsCopyUpdatesActive) {
      setModal(ULTRAPACK_LICENSE_DETAILS_MODAL_ID);
      return;
    }
    setModal(
      isEditorial
        ? SINGLE_IMAGE_EDITORIAL_LICENSE_MODAL_ID
        : SINGLE_IMAGE_CREATIVE_LICENSE_MODAL_ID
    );
  };

  return (
    <ExpandableBuyCard
      title={t('pack_size_images_or_videos', {
        pack_size: selectedPack?.packSize,
      })}
      subtitle={
        <Typography
          variant={TypographyVariant.body.xsmall}
          component={TypographyComponentEnum.DIV}
          data-testid="ultrapack-buy-card-subtitle"
        >
          {t('flexible_commitment_free_pack')}
        </Typography>
      }
      price={selectedPack?.pricePerDownload}
      priceLabel={t('slash_download')}
      badge={showSavingsBadge ? t('save_up_to_fifty_percent') : undefined}
      isExpanded={isExpanded}
      isSelected={isSelected}
      onExpand={onExpand}
      onSelect={onSelect}
      data-testid="ultrapack-buy-card"
    >
      <div className={styles.packSelector}>
        <Typography
          variant={TypographyVariant.body.small}
          component={TypographyComponentEnum.DIV}
        >
          {t('pack_options')}
        </Typography>
        <ButtonGroupSelector
          options={packOptions.map((option) => ({
            value: option.packSize,
            label: t('pack_count_lowercase', { total: option.packSize }),
            testId: `ultrapack-pack-option-${option.packSize}`,
          }))}
          value={selectedPack?.packSize ?? null}
          onChange={onSelectPack}
          ariaLabel={t('pack_options')}
        />
      </div>

      <List
        className={styles.benefitList}
        data-testid="ultrapack-buy-card-benefits"
      >
        {BENEFIT_TRANSLATION_KEYS.map((key) => (
          <ListItem
            key={key}
            className={styles.benefitListItem}
          >
            <CheckRoundedIcon
              className={styles.checkMark}
              size="16px"
            />
            <Typography
              variant={TypographyVariant.body.small}
              component={TypographyComponentEnum.SPAN}
            >
              {t(key)}
            </Typography>
          </ListItem>
        ))}
      </List>

      <Button
        variant={ButtonVariantEnum.LINK}
        size={ButtonSizeEnum.EXTRA_SMALL}
        data-testid="ultrapack-buy-card-view-license-details"
        onClick={handleLicenseDetailsClick}
      >
        <CaretText>{t(VIEW_LICENSE_DETAILS_TRANSLATION_KEY)}</CaretText>
      </Button>

      <div
        className={styles.totalRow}
        data-testid="ultrapack-buy-card-total"
      >
        <Typography
          variant={TypographyVariant.body.small}
          component={TypographyComponentEnum.SPAN}
        >
          {t('total')}
        </Typography>
        <div className={styles.totalPriceGroup}>
          <Typography
            variant={TypographyVariant.body.xsmall}
            component={TypographyComponentEnum.DIV}
            className={styles.priceDisclaimer}
          >
            {t('limited_time_only')}
          </Typography>
          <Typography
            variant={TypographyVariant.body.small}
            component={TypographyComponentEnum.DIV}
          >
            {selectedPack?.total}
          </Typography>
        </div>
      </div>
    </ExpandableBuyCard>
  );
};

export default UltrapackBuyCard;
